import { useState } from "react";
import { cadastrarInteresseService } from "../../../services/cadastrarInteresseService";
import { cadastrarNaoInteresseEmpreend } from "../../../services/cadastrarNaoInteresseEmpreendService";
import { useVerificaPeriodo } from "./useVerificaPeriodo";
import { atualizarInteresseService } from "../../../services/atualizarInteresseService";
import { excluirInteresse } from "../../../services/excluirInteresseService";
import { excluirINaonteresseEmpreendService } from "../../../services/excluirINaonteresseEmpreendService";

export const useInserirInteresse = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const resetError = () => setError(null);
  const resetSuccess = () => setSuccess(null);
  const resetWarning = () => setWarning(null);

  const { verificar } = useVerificaPeriodo();

  const cadastrar = async (
    usuarioId: number,
    interesseData: any,
    empreendData: any
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      // cadastra todos os interesses com seus empreendimentos
      await Promise.all(
        interesseData.periodos.map(async (periodo: any) => {
          // --- Monta dados do interesse ---
          const dataInteresse = {
            capacidade: interesseData.quantidadePessoas,
            dataIni: periodo.inicio,
            dataFim: periodo.fim,
            alugar: +interesseData.objetivos.includes("alugar"),
            trocar: +interesseData.objetivos.includes("trocar"),
            origem: interesseData.origem,
          };

          let inserirInteresse: Awaited<
            ReturnType<typeof cadastrarInteresseService>
          > | null = null;

          // --- Regras de negócio ---
          if (dataInteresse.alugar && !dataInteresse.trocar) {
            // Apenas alugar → cadastra direto
            inserirInteresse = await cadastrarInteresseService(
              usuarioId,
              dataInteresse
            );
          } else if (dataInteresse.trocar) {
            // Troca ou Troca+Alugar → verifica período antes
            const verificarPeriodo = await verificar(usuarioId);

            if (!verificarPeriodo) {
              setWarning(
                " Interesse cadastrado. No entanto, verifiquei aqui que você ainda não indicou um período para troca. Na próxima tela favor clicar em 'Ver períodos' em uma propriedade e disponibilizar."
              );
              //throw new Error("Período inválido para troca"); // 🚨 Interrompe todo o fluxo
            }

            inserirInteresse = await cadastrarInteresseService(
              usuarioId,
              dataInteresse
            );
          }

          // Se não cadastrou (ex: alguma validação extra), não segue
          if (!inserirInteresse) return null;

          // --- Vincula empreendimentos ao interesse criado ---
          const promessasEmpreend = empreendData.selecionados.map(
            (emp: any) => {
              const interesseEmpreendData = {
                empreendId: emp.id,
                interesseId: inserirInteresse!.id,
                interesse: emp.cronograma === false ? 0 : 1,
              };
              return cadastrarNaoInteresseEmpreend(interesseEmpreendData);
            }
          );

          await Promise.all(promessasEmpreend);

          return inserirInteresse;
        })
      );

      setSuccess("Interesses cadastrados com sucesso!");
      return true;
    } catch (error) {
      console.error("Erro ao cadastrar interesse:", error);
      // Mantém a mensagem do warning se for o caso, senão mostra erro genérico
      if (
        !(error instanceof Error && error.message.includes("Período inválido"))
      ) {
        setError("Erro ao cadastrar o interesse. Tente novamente.");
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const atualizar = async (
    usuarioId: number,
    interesseDataOriginal: any, // objeto ou lista de interesses já salvos
    empreendDataOriginal: any,
    interesseData: any, // objeto com periodos/objetivos etc.
    empreendData: any
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      // --- INTERESSES ---
      // 🔹 garante que sempre seja array
      const originais = Array.isArray(interesseDataOriginal)
        ? interesseDataOriginal
        : interesseDataOriginal
        ? [interesseDataOriginal]
        : [];

      const atuais =
        interesseData?.periodos?.map((p: any) => ({
          id: p.id || null,
          capacidade: interesseData.quantidadePessoas,
          dataIni: p.inicio,
          dataFim: p.fim,
          alugar: +interesseData.objetivos.includes("alugar"),
          trocar: +interesseData.objetivos.includes("trocar"),
          origem: interesseData.origem,
        })) || [];

      // console.log("originais", originais);
      // console.log("atuais", atuais);

      // Itens adicionados → sem id ou não encontrados em originais
      const adicionados = atuais.filter(
        (atual: any) =>
          !atual.id || !originais.some((orig: any) => orig.id === atual.id)
      );

      // Itens removidos → estavam em originais mas não estão mais em atuais
      const removidos = originais.filter(
        (orig: any) => !atuais.some((atual: any) => atual.id === orig.id)
      );

      // Itens alterados → mesmos ids mas dados diferentes
      const alterados = atuais.filter((atual: any) =>
        originais.some(
          (orig: any) =>
            atual.id === orig.id &&
            (orig.capacidade !== atual.capacidade ||
              orig.alugar !== atual.alugar ||
              orig.trocar !== atual.trocar ||
              orig.dataIni !== atual.dataIni ||
              orig.dataFim !== atual.dataFim)
        )
      );

      // console.log("Adicionados:", adicionados);
      // console.log("Alterados:", alterados);
      // console.log("Removidos:", removidos);

      // --- EXECUÇÃO ---

      // // Adicionar novos
      for (const novo of adicionados) {
        const novoInteresse = await cadastrarInteresseService(usuarioId, {
          capacidade: novo.capacidade,
          dataIni: novo.dataIni,
          dataFim: novo.dataFim,
          alugar: novo.alugar,
          trocar: novo.trocar,
          origem: novo.origem,
        });

        if (novoInteresse?.id) {
          for (const emp of empreendData?.selecionados || []) {
            await cadastrarNaoInteresseEmpreend({
              empreendId: emp.id,
              interesseId: novoInteresse.id,
              interesse: emp.cronograma === false ? 0 : 1,
            });
          }
        }
      }

      // Atualizar existentes
      for (const alt of alterados) {
        await atualizarInteresseService(alt.id, {
          capacidade: alt.capacidade,
          dataIni: alt.dataIni,
          dataFim: alt.dataFim,
          alugar: alt.alugar,
          trocar: alt.trocar,
          origem: alt.origem,
        });
      }

      // Remover excluídos
      for (const rem of removidos) {
        await excluirInteresse(rem.id);
      }

      //--- EMPREENDIMENTOS ---
      const origEmp = empreendDataOriginal?.selecionados || [];
      const atuEmp = empreendData?.selecionados || [];

      const novosEmp = atuEmp.filter(
        (emp: any) => !origEmp.some((o: any) => o.id === emp.id)
      );
      const removidosEmp = origEmp.filter(
        (emp: any) => !atuEmp.some((a: any) => a.id === emp.id)
      );

      for (const novo of novosEmp) {
        await cadastrarNaoInteresseEmpreend({
          empreendId: novo.id,
          interesseId: interesseData.id,
          interesse: novo.cronograma === false ? 0 : 1,
        });
      }

      for (const rem of removidosEmp) {
        await excluirINaonteresseEmpreendService(rem.id);
      }

      setSuccess("Interesses atualizados com sucesso!");
      return true;
    } catch (error) {
      console.error("Erro ao atualizar interesse:", error);
      if (
        !(error instanceof Error && error.message.includes("Período inválido"))
      ) {
        setError("Erro ao atualizar o interesse. Tente novamente.");
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    cadastrar,
    atualizar,
    isLoading,
    error,
    success,
    warning,
    resetError,
    resetSuccess,
    resetWarning,
  };
};
